import type { ReactNode } from "react"

import { Box, Paper, Typography } from "@mui/material"
import {
  DATA_VIEW_CALENDAR_MIN_WIDTH,
  DATA_VIEW_CALENDAR_ROW_HEIGHT,
  DATA_VIEW_HEADER_HEIGHT,
  getDataViewHeaderStyles,
} from "components/common/dataViewStyles"
import { FlexColumn } from "components/common/StyledComponents"
import { DateTime } from "luxon"
import { useMemo } from "react"
import { getCalendarDateRange } from "utils/dateRange"

type Props = {
  endDate: DateTime
  getDayAriaLabel: (day: DateTime) => string
  onDayClick: (day: DateTime) => void
  renderDay: (day: DateTime) => ReactNode
  startDate: DateTime
}

const DAY_NAMES = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7", "Chủ nhật"]

const DataViewCalendar = ({ endDate, getDayAriaLabel, onDayClick, renderDay, startDate }: Props) => {
  const days = useMemo(() => getCalendarDateRange(startDate, endDate), [endDate, startDate])

  return (
    <Paper
      sx={{
        borderRadius: 2,
        flex: 1,
        minHeight: 0,
        overflowX: "auto",
        overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
      }}
      variant="outlined"
    >
      <FlexColumn height="100%" minWidth={DATA_VIEW_CALENDAR_MIN_WIDTH}>
        <Box
          flexShrink={0}
          position="relative"
          sx={(theme) => ({
            ...getDataViewHeaderStyles(theme),
            overflowY: "auto",
            scrollbarGutter: "stable",
          })}
          zIndex={2}
        >
          <Box display="grid" gridTemplateColumns="repeat(7, minmax(0, 1fr))" height={DATA_VIEW_HEADER_HEIGHT}>
            {DAY_NAMES.map((name, index) => (
              <Box
                alignItems="center"
                display="flex"
                justifyContent="center"
                key={name}
                sx={(theme) => ({
                  borderRight: index === DAY_NAMES.length - 1 ? "none" : `1px solid ${theme.palette.divider}`,
                })}
              >
                <Typography color="text.secondary" variant="subtitle2">
                  {name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          alignContent="start"
          display="grid"
          flex={1}
          gridAutoRows={DATA_VIEW_CALENDAR_ROW_HEIGHT}
          gridTemplateColumns="repeat(7, minmax(0, 1fr))"
          minHeight={0}
          sx={{ overflowY: "auto", scrollbarGutter: "stable" }}
        >
          {days.map((day, index) => {
            const isLastColumn = (index + 1) % 7 === 0
            const isLastRow = index >= days.length - 7
            const isOutOfRange = day < startDate.startOf("day") || day > endDate.endOf("day")

            const handleOpen = () => onDayClick(day)

            return (
              <Box
                aria-label={getDayAriaLabel(day)}
                bgcolor={isOutOfRange ? "action.hover" : "background.paper"}
                display="flex"
                flexDirection="column"
                gap={0.75}
                key={day.toISO()}
                onClick={handleOpen}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return
                  event.preventDefault()
                  handleOpen()
                }}
                overflow="hidden"
                p={1}
                position="relative"
                role="button"
                sx={(theme) => ({
                  "&:hover": { bgcolor: "action.hover" },
                  borderBottom: isLastRow ? "none" : `1px solid ${theme.palette.divider}`,
                  borderRight: isLastColumn ? "none" : `1px solid ${theme.palette.divider}`,
                  cursor: "pointer",
                  opacity: isOutOfRange ? theme.palette.action.disabledOpacity : 1,
                  transition: theme.transitions.create("background-color", {
                    duration: theme.transitions.duration.shortest,
                  }),
                })}
                tabIndex={0}
              >
                {renderDay(day)}
              </Box>
            )
          })}
        </Box>
      </FlexColumn>
    </Paper>
  )
}

export default DataViewCalendar
